import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { EMPTY_FILTERS } from '../core/model/search';
import type { LocationOption, SearchCriteria, SearchFilters, SortOption } from '../core/model/search';
import type { LocalDate, LocalTime } from '../core/util/datetime';
import { loadCriteria, saveCriteria } from '../data/local/criteriaStore';

interface CriteriaValue {
  readonly criteria: SearchCriteria;
  setQuery(query: string): void;
  setLocation(location: LocationOption): void;
  setDate(date: LocalDate): void;
  setTime(time: LocalTime): void;
  setPartySize(size: number): void;
  setFilters(filters: SearchFilters): void;
  clearFilters(): void;
  setSort(sort: SortOption): void;
}

const CriteriaContext = createContext<CriteriaValue | null>(null);

/**
 * Los criterios de búsqueda viven por encima de las pantallas y se persisten.
 *
 * Es lo que hace que elegir "4 personas, viernes 21:00" en el Inicio siga vigente al abrir un
 * restaurante y al empezar la reserva: los tres pasos leen el mismo estado en lugar de pasarse
 * los valores por la URL.
 */
export function CriteriaProvider({ children }: { children: ReactNode }) {
  const [criteria, setCriteria] = useState<SearchCriteria>(() => loadCriteria());

  useEffect(() => {
    saveCriteria(criteria);
  }, [criteria]);

  const value = useMemo<CriteriaValue>(
    () => ({
      criteria,
      setQuery: (query) => setCriteria((c) => ({ ...c, query })),
      setLocation: (location) => setCriteria((c) => ({ ...c, location })),
      setDate: (date) => setCriteria((c) => ({ ...c, date })),
      setTime: (time) => setCriteria((c) => ({ ...c, time })),
      setPartySize: (partySize) => setCriteria((c) => ({ ...c, partySize })),
      setFilters: (filters) => setCriteria((c) => ({ ...c, filters })),
      clearFilters: () => setCriteria((c) => ({ ...c, filters: EMPTY_FILTERS })),
      setSort: (sort) => setCriteria((c) => ({ ...c, sort })),
    }),
    [criteria],
  );

  return <CriteriaContext.Provider value={value}>{children}</CriteriaContext.Provider>;
}

export function useCriteria(): CriteriaValue {
  const value = useContext(CriteriaContext);
  if (!value) throw new Error('useCriteria debe usarse dentro de <CriteriaProvider>');
  return value;
}
